/* eslint-disable no-param-reassign */
/**
 * Directive `v-tooltip` durcie — fork de `primevue/tooltip` 3.23.0 (licence MIT).
 *
 * Pourquoi un fork : sur Chrome, quand l'élément survolé est détaché puis réinséré dans le
 * DOM (réordonnancement d'une liste `v-for` à clé, déplacement de nœud), le navigateur
 * n'émet plus `mouseleave`. La directive d'origine ne s'en remet pas :
 *   - `show()` recrée systématiquement une bulle avec un nouvel identifiant et écrase la
 *     référence vers la précédente, qui reste orpheline dans `document.body` (bulle « figée ») ;
 *   - `updated()` retire les écouteurs quand la valeur devient vide sans masquer la bulle ouverte.
 *
 * Corrections apportées (comportement visuel, classes CSS et options inchangés) :
 *   1. `show()` retire toujours la bulle existante de l'hôte avant d'en créer une nouvelle ;
 *   2. un balayage global (souris, clavier, défilement, redimensionnement) retire toute bulle
 *      dont l'hôte n'est plus dans le DOM, plus survolé (`:hover`) ou plus focalisé ;
 *   3. `updated()` masque la bulle quand la valeur devient vide ;
 *   4. la bulle ne capte plus les événements souris (`pointer-events: none`) : même orpheline,
 *      elle ne peut plus bloquer un clic.
 *
 * Branchement : l'alias webpack `primevue/tooltip` (vue.config.js) et le mapper Jest pointent
 * ici, donc chaque `import Tooltip from 'primevue/tooltip'` du projet utilise cette version.
 * À retirer avec l'alias lors d'une montée de version de PrimeVue.
 */
import type { DirectiveBinding, ObjectDirective } from 'vue';
import {
  ObjectUtils,
  DomHandler,
  ZIndexUtils,
  UniqueComponentId,
  ConnectedOverlayScrollHandler,
} from 'primevue/utils';

export interface TooltipOptions {
  value?: string;
  disabled?: boolean;
  escape?: boolean;
  class?: string;
  fitContent?: boolean;
  id?: string;
}

type TooltipValue = string | TooltipOptions | null | undefined;

type TooltipModifiers = Record<string, boolean>;

/** Élément hôte enrichi de l'état de la directive (mêmes noms de propriétés que PrimeVue). */
interface TooltipHost extends HTMLElement {
  $_ptooltipModifiers?: TooltipModifiers;
  $_ptooltipValue?: string;
  $_ptooltipDisabled?: boolean;
  $_ptooltipEscape?: boolean;
  $_ptooltipClass?: string | null;
  $_ptooltipFitContent?: boolean;
  $_ptooltipIdAttr?: string;
  $_ptooltipZIndex?: number;
  $_ptooltipId?: string | null;
  $_ptooltipElement?: HTMLElement | null;
  $_ptooltipScrollHandler?: ConnectedOverlayScrollHandler | null;
}

const DEFAULT_Z_INDEX = 1100;

/** Hôtes dont une bulle est actuellement affichée (source du balayage global). */
const openHosts = new Set<TooltipHost>();

let globalListenersBound = false;
let sweepScheduled = false;

// ---------------------------------------------------------------------------
// Utilitaires
// ---------------------------------------------------------------------------

function getTarget(el: HTMLElement): TooltipHost {
  return DomHandler.hasClass(el, 'p-inputwrapper') ? DomHandler.findSingle(el, 'input') : el;
}

function getModifiers(binding: DirectiveBinding<TooltipValue>): TooltipModifiers {
  if (binding.modifiers && Object.keys(binding.modifiers).length) {
    return binding.modifiers;
  }

  // Forme dynamique `v-tooltip:[{ event: 'focus', position: 'top' }]`
  const arg = binding.arg as unknown;
  if (arg && typeof arg === 'object') {
    return Object.entries(arg as Record<string, string>).reduce((acc: TooltipModifiers, [key, val]) => {
      if (key === 'event' || key === 'position') acc[val] = true;
      return acc;
    }, {});
  }

  return {};
}

function getTooltipElement(el: TooltipHost): HTMLElement | null {
  if (el.$_ptooltipElement && el.$_ptooltipElement.isConnected) {
    return el.$_ptooltipElement;
  }
  return el.$_ptooltipId ? document.getElementById(el.$_ptooltipId) : null;
}

/** L'hôte justifie-t-il encore l'affichage de sa bulle ? */
function isHostActive(el: TooltipHost): boolean {
  if (!el.isConnected) return false;
  if (el.$_ptooltipModifiers && el.$_ptooltipModifiers.focus) {
    return document.activeElement === el;
  }
  return el.matches(':hover');
}

// ---------------------------------------------------------------------------
// Retrait
// ---------------------------------------------------------------------------

function unbindScrollListener(el: TooltipHost): void {
  if (el.$_ptooltipScrollHandler) {
    el.$_ptooltipScrollHandler.unbindScrollListener();
  }
}

function remove(el: TooltipHost | null | undefined): void {
  if (!el) return;

  const tooltipElement = getTooltipElement(el);
  if (tooltipElement && tooltipElement.parentElement) {
    ZIndexUtils.clear(tooltipElement);
    tooltipElement.parentElement.removeChild(tooltipElement);
  }

  el.$_ptooltipId = null;
  el.$_ptooltipElement = null;
  openHosts.delete(el);
}

function hide(el: TooltipHost): void {
  remove(el);
  unbindScrollListener(el);
}

/** Retire les bulles dont l'hôte a disparu ou n'est plus survolé/focalisé. */
function sweep(): void {
  Array.from(openHosts).forEach((host) => {
    if (!isHostActive(host)) {
      hide(host);
    }
  });
}

function scheduleSweep(): void {
  if (sweepScheduled || openHosts.size === 0) return;
  sweepScheduled = true;
  window.requestAnimationFrame(() => {
    sweepScheduled = false;
    sweep();
  });
}

function hideAllOnResize(): void {
  if (DomHandler.isTouchDevice()) return;
  Array.from(openHosts).forEach((host) => hide(host));
}

function bindGlobalListeners(): void {
  if (globalListenersBound || typeof document === 'undefined') return;
  globalListenersBound = true;

  const passiveCapture: AddEventListenerOptions = { capture: true, passive: true };
  document.addEventListener('mousemove', scheduleSweep, passiveCapture);
  document.addEventListener('mousedown', scheduleSweep, passiveCapture);
  document.addEventListener('touchstart', scheduleSweep, passiveCapture);
  document.addEventListener('keydown', scheduleSweep, passiveCapture);
  document.addEventListener('scroll', scheduleSweep, passiveCapture);
  window.addEventListener('resize', hideAllOnResize);
}

// ---------------------------------------------------------------------------
// Création et positionnement (identiques à PrimeVue 3.23.0)
// ---------------------------------------------------------------------------

function create(el: TooltipHost): HTMLElement {
  const id = el.$_ptooltipIdAttr ? el.$_ptooltipIdAttr : `${UniqueComponentId()}_tooltip`;

  el.$_ptooltipId = id;

  const container = document.createElement('div');
  container.id = id;

  const tooltipArrow = document.createElement('div');
  tooltipArrow.className = 'p-tooltip-arrow';
  container.appendChild(tooltipArrow);

  const tooltipText = document.createElement('div');
  tooltipText.className = 'p-tooltip-text';

  if (el.$_ptooltipEscape) {
    tooltipText.innerHTML = el.$_ptooltipValue || '';
  } else {
    tooltipText.innerHTML = '';
    tooltipText.appendChild(document.createTextNode(el.$_ptooltipValue || ''));
  }

  container.setAttribute('role', 'tooltip');
  container.appendChild(tooltipText);
  document.body.appendChild(container);

  container.style.display = 'inline-block';
  // Une bulle n'est jamais interactive : elle ne doit pas intercepter les clics.
  container.style.pointerEvents = 'none';

  if (el.$_ptooltipFitContent) {
    container.style.width = 'fit-content';
  }

  el.$_ptooltipElement = container;

  return container;
}

function getHostOffset(el: TooltipHost): { left: number; top: number } {
  const offset = el.getBoundingClientRect();
  return {
    left: offset.left + DomHandler.getWindowScrollLeft(),
    top: offset.top + DomHandler.getWindowScrollTop(),
  };
}

function preAlign(el: TooltipHost, position: string): HTMLElement | null {
  const tooltipElement = getTooltipElement(el);
  if (!tooltipElement) return null;

  tooltipElement.style.left = '-999px';
  tooltipElement.style.top = '-999px';
  tooltipElement.className = `p-tooltip p-component p-tooltip-${position} ${el.$_ptooltipClass || ''}`;

  return tooltipElement;
}

function alignRight(el: TooltipHost): void {
  const tooltipElement = preAlign(el, 'right');
  if (!tooltipElement) return;

  const hostOffset = getHostOffset(el);
  const left = hostOffset.left + DomHandler.getOuterWidth(el, false);
  const top = hostOffset.top + (DomHandler.getOuterHeight(el, false) - DomHandler.getOuterHeight(tooltipElement, false)) / 2;

  tooltipElement.style.left = `${left}px`;
  tooltipElement.style.top = `${top}px`;
}

function alignLeft(el: TooltipHost): void {
  const tooltipElement = preAlign(el, 'left');
  if (!tooltipElement) return;

  const hostOffset = getHostOffset(el);
  const left = hostOffset.left - DomHandler.getOuterWidth(tooltipElement, false);
  const top = hostOffset.top + (DomHandler.getOuterHeight(el, false) - DomHandler.getOuterHeight(tooltipElement, false)) / 2;

  tooltipElement.style.left = `${left}px`;
  tooltipElement.style.top = `${top}px`;
}

function alignTop(el: TooltipHost): void {
  const tooltipElement = preAlign(el, 'top');
  if (!tooltipElement) return;

  const hostOffset = getHostOffset(el);
  const left = hostOffset.left + (DomHandler.getOuterWidth(el, false) - DomHandler.getOuterWidth(tooltipElement, false)) / 2;
  const top = hostOffset.top - DomHandler.getOuterHeight(tooltipElement, false);

  tooltipElement.style.left = `${left}px`;
  tooltipElement.style.top = `${top}px`;
}

function alignBottom(el: TooltipHost): void {
  const tooltipElement = preAlign(el, 'bottom');
  if (!tooltipElement) return;

  const hostOffset = getHostOffset(el);
  const left = hostOffset.left + (DomHandler.getOuterWidth(el, false) - DomHandler.getOuterWidth(tooltipElement, false)) / 2;
  const top = hostOffset.top + DomHandler.getOuterHeight(el, false);

  tooltipElement.style.left = `${left}px`;
  tooltipElement.style.top = `${top}px`;
}

function isOutOfBounds(el: TooltipHost): boolean {
  const tooltipElement = getTooltipElement(el);
  if (!tooltipElement) return false;

  const offset = tooltipElement.getBoundingClientRect();
  const targetTop = offset.top;
  const targetLeft = offset.left;
  const width = DomHandler.getOuterWidth(tooltipElement, false);
  const height = DomHandler.getOuterHeight(tooltipElement, false);
  const viewport = DomHandler.getViewport();

  return targetLeft + width > viewport.width || targetLeft < 0 || targetTop < 0 || targetTop + height > viewport.height;
}

function align(el: TooltipHost): void {
  const modifiers = el.$_ptooltipModifiers || {};

  if (modifiers.top) {
    alignTop(el);

    if (isOutOfBounds(el)) {
      alignBottom(el);

      if (isOutOfBounds(el)) {
        alignTop(el);
      }
    }
  } else if (modifiers.left) {
    alignLeft(el);

    if (isOutOfBounds(el)) {
      alignRight(el);

      if (isOutOfBounds(el)) {
        alignTop(el);

        if (isOutOfBounds(el)) {
          alignBottom(el);

          if (isOutOfBounds(el)) {
            alignLeft(el);
          }
        }
      }
    }
  } else if (modifiers.bottom) {
    alignBottom(el);

    if (isOutOfBounds(el)) {
      alignTop(el);

      if (isOutOfBounds(el)) {
        alignBottom(el);
      }
    }
  } else {
    alignRight(el);

    if (isOutOfBounds(el)) {
      alignLeft(el);

      if (isOutOfBounds(el)) {
        alignTop(el);

        if (isOutOfBounds(el)) {
          alignBottom(el);

          if (isOutOfBounds(el)) {
            alignRight(el);
          }
        }
      }
    }
  }
}

// ---------------------------------------------------------------------------
// Affichage
// ---------------------------------------------------------------------------

function bindScrollListener(el: TooltipHost): void {
  if (!el.$_ptooltipScrollHandler) {
    el.$_ptooltipScrollHandler = new ConnectedOverlayScrollHandler(el, () => {
      hide(el);
    });
  }

  el.$_ptooltipScrollHandler.bindScrollListener();
}

function show(el: TooltipHost): void {
  if (el.$_ptooltipDisabled || !el.isConnected) {
    return;
  }

  // Correction 1 : jamais deux bulles pour le même hôte.
  remove(el);

  const tooltipElement = create(el);

  align(el);
  DomHandler.fadeIn(tooltipElement, 250);

  bindScrollListener(el);
  ZIndexUtils.set('tooltip', tooltipElement, el.$_ptooltipZIndex);

  openHosts.add(el);
  bindGlobalListeners();
}

// ---------------------------------------------------------------------------
// Événements de l'hôte
// ---------------------------------------------------------------------------

function onMouseEnter(event: Event): void {
  show(event.currentTarget as TooltipHost);
}

function onMouseLeave(event: Event): void {
  hide(event.currentTarget as TooltipHost);
}

function onFocus(event: Event): void {
  show(event.currentTarget as TooltipHost);
}

function onBlur(event: Event): void {
  hide(event.currentTarget as TooltipHost);
}

function onClick(event: Event): void {
  hide(event.currentTarget as TooltipHost);
}

function onKeydown(event: KeyboardEvent): void {
  if (event.code === 'Escape') hide(event.currentTarget as TooltipHost);
}

function bindEvents(el: TooltipHost): void {
  const modifiers = el.$_ptooltipModifiers || {};

  if (modifiers.focus) {
    el.addEventListener('focus', onFocus);
    el.addEventListener('blur', onBlur);
  } else {
    el.addEventListener('mouseenter', onMouseEnter);
    el.addEventListener('mouseleave', onMouseLeave);
    el.addEventListener('click', onClick);
  }

  el.addEventListener('keydown', onKeydown);
}

function unbindEvents(el: TooltipHost): void {
  const modifiers = el.$_ptooltipModifiers || {};

  if (modifiers.focus) {
    el.removeEventListener('focus', onFocus);
    el.removeEventListener('blur', onBlur);
  } else {
    el.removeEventListener('mouseenter', onMouseEnter);
    el.removeEventListener('mouseleave', onMouseLeave);
    el.removeEventListener('click', onClick);
  }

  el.removeEventListener('keydown', onKeydown);
}

// ---------------------------------------------------------------------------
// Lecture des options de la directive
// ---------------------------------------------------------------------------

/** Applique la valeur de la directive à l'hôte ; renvoie `false` si la bulle doit être désactivée. */
function applyOptions(target: TooltipHost, binding: DirectiveBinding<TooltipValue>): boolean {
  const { value } = binding;

  if (typeof value === 'string') {
    target.$_ptooltipValue = value;
    target.$_ptooltipDisabled = false;
    target.$_ptooltipEscape = false;
    target.$_ptooltipClass = null;
    target.$_ptooltipFitContent = true;
    target.$_ptooltipIdAttr = '';
    return true;
  }

  if (value && typeof value === 'object') {
    if (ObjectUtils.isEmpty(value.value) || (value.value as string).trim() === '') {
      return false;
    }
    target.$_ptooltipValue = value.value;
    target.$_ptooltipDisabled = typeof value.disabled === 'boolean' ? value.disabled : false;
    target.$_ptooltipEscape = typeof value.escape === 'boolean' ? value.escape : false;
    target.$_ptooltipClass = value.class ?? null;
    target.$_ptooltipFitContent = typeof value.fitContent === 'boolean' ? value.fitContent : true;
    target.$_ptooltipIdAttr = value.id || '';
    return true;
  }

  return false;
}

function resolveZIndex(binding: DirectiveBinding<TooltipValue>): number {
  const instance = binding.instance as { $primevue?: { config?: { zIndex?: { tooltip?: number } } } } | null;
  const zIndex = instance?.$primevue?.config?.zIndex?.tooltip;
  return typeof zIndex === 'number' ? zIndex : DEFAULT_Z_INDEX;
}

// ---------------------------------------------------------------------------
// Directive
// ---------------------------------------------------------------------------

const Tooltip: ObjectDirective<HTMLElement, TooltipValue> = {
  beforeMount(el, binding) {
    const target = getTarget(el);

    target.$_ptooltipModifiers = getModifiers(binding);

    if (!binding.value) return;
    if (!applyOptions(target, binding)) return;

    target.$_ptooltipZIndex = resolveZIndex(binding);
    bindEvents(target);
  },

  unmounted(el) {
    const target = getTarget(el);

    remove(target);
    unbindEvents(target);

    if (target.$_ptooltipScrollHandler) {
      target.$_ptooltipScrollHandler.destroy();
      target.$_ptooltipScrollHandler = null;
    }
  },

  updated(el, binding) {
    const target = getTarget(el);
    target.$_ptooltipModifiers = getModifiers(binding);

    if (!binding.value || !applyOptions(target, binding)) {
      // Correction 3 : une valeur devenue vide masque la bulle avant de couper les écouteurs.
      hide(target);
      unbindEvents(target);
      return;
    }

    if (target.$_ptooltipDisabled) {
      hide(target);
    }

    bindEvents(target);
  },
};

export default Tooltip;
